export const fetchNominatimPredictions = async (input) => {
  if (!input || input.length < 3) return [];

  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(input)}&format=json&limit=5&addressdetails=1`
    );

    if (!response.ok) return [];

    const results = await response.json();

    return results.map((result) => ({
      description: formatAddressForBackend(result),
      place_id: result.place_id,
      raw: result,
    }));
  } catch (error) {
    console.error('Nominatim API error:', error);
    return [];
  }
};

export const createDebouncer = (wait = 300) => {
  let timeout;
  return (func) => {
    return (...args) => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };
};

export const searchOsmAddresses = async (query, currentRequestId) => {
  if (!query || query.length < 3) {
    return { suggestions: [], showDropdown: false };
  }

  try {
    const results = await fetchNominatimPredictions(query);
    return {
      suggestions: results,
      showDropdown: results.length > 0,
      requestId: currentRequestId,
    };
  } catch (error) {
    console.error('OSM search error:', error);
    return { suggestions: [], showDropdown: false, requestId: currentRequestId };
  }
};

// Format address to match your backend's expected format
const formatAddressForBackend = (result) => {
  const addr = result.address;
  if (!addr) return result.display_name;

  // Build address components in the order your backend expects
  let components = [];

  // House number + street
  const streetParts = [];
  if (addr.house_number) streetParts.push(addr.house_number);
  if (addr.road) {
    let road = addr.road
      .replace(/\bStreet\b/g, 'St')
      .replace(/\bRoad\b/g, 'Rd')
      .replace(/\bAvenue\b/g, 'Ave')
      .replace(/\bLane\b/g, 'Ln')
      .replace(/\bBoulevard\b/g, 'Blvd')
      .replace(/\bDrive\b/g, 'Dr')
      .replace(/\bParkway\b/g, 'Pkwy');
    streetParts.push(road);
  }
  if (streetParts.length) components.push(streetParts.join(' '));

  // City
  const city = addr.city || addr.town || addr.village || addr.hamlet;
  if (city) components.push(city);

  // State (convert to abbreviation)
  if (addr.state) {
    const stateAbbr = getStateAbbreviation(addr.state);
    components.push(stateAbbr || addr.state);
  }

  // ZIP code
  if (addr.postcode) components.push(addr.postcode);

  // Country (use USA instead of United States)
  const country = addr.country === 'United States' ? 'USA' : addr.country;
  if (country) components.push(country);

  return components.join(', ');
};

// US state name to abbreviation mapping
const getStateAbbreviation = (stateName) => {
  const states = {
    Alabama: 'AL',
    Alaska: 'AK',
    Arizona: 'AZ',
    Arkansas: 'AR',
    California: 'CA',
    Colorado: 'CO',
    Connecticut: 'CT',
    Delaware: 'DE',
    'District of Columbia': 'DC',
    Florida: 'FL',
    Georgia: 'GA',
    Hawaii: 'HI',
    Idaho: 'ID',
    Illinois: 'IL',
    Indiana: 'IN',
    Iowa: 'IA',
    Kansas: 'KS',
    Kentucky: 'KY',
    Louisiana: 'LA',
    Maine: 'ME',
    Maryland: 'MD',
    Massachusetts: 'MA',
    Michigan: 'MI',
    Minnesota: 'MN',
    Mississippi: 'MS',
    Missouri: 'MO',
    Montana: 'MT',
    Nebraska: 'NE',
    Nevada: 'NV',
    'New Hampshire': 'NH',
    'New Jersey': 'NJ',
    'New Mexico': 'NM',
    'New York': 'NY',
    'North Carolina': 'NC',
    'North Dakota': 'ND',
    Ohio: 'OH',
    Oklahoma: 'OK',
    Oregon: 'OR',
    Pennsylvania: 'PA',
    'Rhode Island': 'RI',
    'South Carolina': 'SC',
    'South Dakota': 'SD',
    Tennessee: 'TN',
    Texas: 'TX',
    Utah: 'UT',
    Vermont: 'VT',
    Virginia: 'VA',
    Washington: 'WA',
    'West Virginia': 'WV',
    Wisconsin: 'WI',
    Wyoming: 'WY',
  };
  return states[stateName];
};
